// Reprodução do percurso do veículo (Fase C). Anima um cursor ao longo
// dos pontos GPS do dia, com play/pausa, recomeçar, velocidade (1×/2×/4×)
// e barra de progresso arrastável. Reporta o ponto actual via `onCursor`
// — o mapa desenha o marcador móvel e o troço já percorrido.

import React, { useState, useEffect, useRef, useMemo } from 'react';

const SPEEDS = [0.25, 0.5, 1, 2, 4];
const SPEED_LABEL = { 0.25: '¼×', 0.5: '½×', 1: '1×', 2: '2×', 4: '4×' };
const BASE_MS = 420; // intervalo base entre pontos a 1× (mais lento = mais suave)

// Rumo (graus) de a → b, para orientar o marcador.
function bearing(a, b) {
  const toR = (d) => (d * Math.PI) / 180;
  const dLng = toR(b.lng - a.lng);
  const y = Math.sin(dLng) * Math.cos(toR(b.lat));
  const x = Math.cos(toR(a.lat)) * Math.sin(toR(b.lat))
    - Math.sin(toR(a.lat)) * Math.cos(toR(b.lat)) * Math.cos(dLng);
  return (((Math.atan2(y, x) * 180) / Math.PI) + 360) % 360;
}

const fmtClock = (ms) => new Date(ms).toLocaleTimeString('pt-PT', {
  hour: '2-digit', minute: '2-digit', second: '2-digit',
});

// Rumo em graus → rótulo cardinal PT (N, NE, E, SE, S, SO, O, NO).
const cardinal = (deg) => ['N', 'NE', 'E', 'SE', 'S', 'SO', 'O', 'NO'][Math.round((((Number(deg) || 0) % 360) + 360) % 360 / 45) % 8];

export default function VehicleHistoryPlayback({ points = [], onCursor, follow = false, onToggleFollow, events }) {
  const total = (points || []).length;
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [started, setStarted] = useState(false);
  const [repeat, setRepeat] = useState(false); // 🔁 ida-e-volta (bounce) contínuo
  const dirRef = useRef(1); // sentido da reprodução: +1 avança, -1 recua (bounce)
  const idxRef = useRef(0); // idx corrente (autoridade no loop de animação)
  const fracRef = useRef(0); // fração 0..1 entre idx e idx+dir (interpolação)
  useEffect(() => { idxRef.current = idx; }, [idx]);

  // onCursor pode mudar de identidade a cada render — guardamos em ref.
  const cbRef = useRef(onCursor);
  useEffect(() => { cbRef.current = onCursor; }, [onCursor]);

  // Último rumo válido — para MANTER a direção quando o veículo está parado
  // (evita o ícone saltar para Norte / andar de lado nas paragens).
  const lastHeadingRef = useRef(0);
  // O feed dá rumo GPS (course)? Se sim, é mais fiável que o bearing calculado.
  const hasCourse = useMemo(() => (points || []).some(p => Number(p.heading) > 0), [points]);

  // Reporta ao mapa a posição INTERPOLADA entre o ponto `i` e o seguinte
  // (no sentido actual), com o rumo calculado. `frac` 0..1 = quanto já andou.
  const emitCursor = (i, frac = 0) => {
    const cb = cbRef.current; if (!cb) return;
    const p = points[i];
    if (!p) { cb(null); return; }
    const lat0 = Number(p.lat), lng0 = Number(p.lng);
    if (!Number.isFinite(lat0) || !Number.isFinite(lng0)) { cb(null); return; }
    const dir = dirRef.current || 1;
    const q = points[Math.max(0, Math.min(total - 1, i + dir))] || p;
    const lat1 = Number(q.lat), lng1 = Number(q.lng);
    const f = Math.max(0, Math.min(1, frac));
    const lat = Number.isFinite(lat1) ? lat0 + (lat1 - lat0) * f : lat0;
    const lng = Number.isFinite(lng1) ? lng0 + (lng1 - lng0) * f : lng0;
    // Rumo: 1) course GPS (fiável, igual aos marcadores ao vivo); 2) bearing
    // suavizado ±2 pontos; 3) parado → mantém o último rumo (não salta p/ Norte).
    const spd = Number(p.speed) || 0;
    let heading = lastHeadingRef.current;
    if (hasCourse) {
      if (spd > 1 && Number.isFinite(Number(p.heading))) heading = ((Number(p.heading) % 360) + 360) % 360;
    } else {
      const a = points[Math.max(0, i - 2)] || p;
      const b = points[Math.min(total - 1, i + 2)] || p;
      const alat = Number(a.lat), alng = Number(a.lng), blat = Number(b.lat), blng = Number(b.lng);
      const moved = Number.isFinite(alat) && Number.isFinite(blat)
        && (Math.abs(alat - blat) > 1e-5 || Math.abs(alng - blng) > 1e-5);
      if (moved && spd > 1) heading = bearing({ lat: alat, lng: alng }, { lat: blat, lng: blng });
    }
    if (spd > 1) lastHeadingRef.current = heading;
    cb({ lat, lng, idx: i, total, heading, speed: spd, timestamp: p.timestamp });
  };

  // Reset ao mudar o conjunto de pontos (novo dia / veículo).
  useEffect(() => { setIdx(0); setPlaying(false); setStarted(false); lastHeadingRef.current = 0; dirRef.current = 1; fracRef.current = 0; }, [points]);

  // Motor de reprodução SUAVE (requestAnimationFrame + interpolação). Avança
  // uma fração contínua; ao cruzar um ponto passa ao seguinte (ou inverte, em
  // "ida e volta"); emite a posição interpolada ~20×/s. Sem repetir, pára no fim.
  useEffect(() => {
    if (!playing || total < 2) return undefined;
    let raf, last = performance.now(), sinceEmit = 0, stopped = false;
    const stepMs = Math.max(60, BASE_MS / speed); // tempo por ponto
    const tick = (now) => {
      const dt = now - last; last = now;
      fracRef.current += dt / stepMs;
      while (fracRef.current >= 1 && !stopped) {
        fracRef.current -= 1;
        let next = idxRef.current + dirRef.current;
        if (next > total - 1) {
          if (repeat) { dirRef.current = -1; next = Math.max(0, total - 2); }
          else { idxRef.current = total - 1; fracRef.current = 0; setIdx(total - 1); setPlaying(false); stopped = true; break; }
        } else if (next < 0) {
          if (repeat) { dirRef.current = 1; next = Math.min(total - 1, 1); }
          else { idxRef.current = 0; fracRef.current = 0; setIdx(0); setPlaying(false); stopped = true; break; }
        }
        idxRef.current = next; setIdx(next);
      }
      sinceEmit += dt;
      if (sinceEmit >= 50) { sinceEmit = 0; emitCursor(idxRef.current, stopped ? 0 : fracRef.current); }
      if (!stopped) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => { stopped = true; if (raf) cancelAnimationFrame(raf); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, speed, total, repeat]);

  // Fora da reprodução (scrub, passo, teclado), reporta o ponto exacto.
  useEffect(() => {
    if (!started) { if (cbRef.current) cbRef.current(null); return; }
    if (playing) return; // a animação trata durante a reprodução
    emitCursor(idx, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, started, playing, points, total, hasCourse]);

  // Limpa o cursor ao desmontar (fecho do painel).
  useEffect(() => () => { if (cbRef.current) cbRef.current(null); }, []);

  const atEnd = idx >= total - 1;
  const jump = (i) => { const v = Math.max(0, Math.min(total - 1, i)); idxRef.current = v; fracRef.current = 0; setIdx(v); setStarted(true); };
  const toggle = () => {
    if (atEnd && !repeat) { jump(0); dirRef.current = 1; }
    setStarted(true);
    setPlaying((v) => !v);
  };
  const restart = () => { jump(0); dirRef.current = 1; setPlaying(true); };
  const goStart = () => { jump(0); dirRef.current = 1; };
  const goEnd = () => { setPlaying(false); jump(total - 1); };
  const step = (d) => { setPlaying(false); jump(idxRef.current + d); };

  // Atalhos de teclado (Espaço, ← →, Home/End) — ignora quando se escreve
  // num campo (input/select) para não colidir com os seletores de data.
  useEffect(() => {
    if (total < 2) return undefined;
    const onKey = (e) => {
      const t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'SELECT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      if (e.code === 'Space') { e.preventDefault(); toggle(); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); step(-1); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); step(1); }
      else if (e.key === 'Home') { e.preventDefault(); goStart(); }
      else if (e.key === 'End') { e.preventDefault(); goEnd(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total, atEnd, repeat, playing]);

  if (total < 2) return null;

  const cur = points[idx] || {};
  const onScrub = (e) => { jump(Number(e.target.value)); };
  const pct = (i) => total > 1 ? (i / (total - 1)) * 100 : 0;
  const stops = events?.stops || [];
  const speeding = events?.speeding || [];

  const btn = {
    border: 'none', cursor: 'pointer', borderRadius: 6,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  };
  const mini = (bg, color) => ({ ...btn, width: 26, height: 24, background: bg, color, fontSize: 12 });

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 4px 1px' }}>
      <button type="button" onClick={goStart} title="Ir ao início (Home)" style={mini('#e2e8f0', '#475569')}>⏮</button>
      <button type="button" onClick={toggle} title={playing ? 'Pausar (Espaço)' : 'Reproduzir (Espaço)'}
        style={{ ...btn, width: 32, height: 24, background: playing ? '#ef4444' : '#2563eb', color: '#fff', fontSize: 11 }}>
        {playing ? '❚❚' : '▶'}
      </button>
      <button type="button" onClick={goEnd} title="Ir ao fim (End)" style={mini('#e2e8f0', '#475569')}>⏭</button>
      <button type="button" onClick={restart} title="Recomeçar" style={mini('#e2e8f0', '#475569')}>↺</button>
      <button type="button" onClick={() => setRepeat(v => !v)} title="Repetir (ida e volta)"
        style={mini(repeat ? '#2563eb' : '#e2e8f0', repeat ? '#fff' : '#475569')}>🔁</button>
      {onToggleFollow && (
        <button type="button" onClick={onToggleFollow} title="Seguir a viatura (câmara acompanha)"
          style={mini(follow ? '#16a34a' : '#e2e8f0', follow ? '#fff' : '#475569')}>📍</button>
      )}
      <div style={{ flex: 1, minWidth: 60, position: 'relative', display: 'flex', alignItems: 'center' }}>
        {/* Faixa de eventos — paragens (cinza) e excessos (vermelho); clica para saltar */}
        {(stops.length > 0 || speeding.length > 0) && (
          <div style={{ position: 'absolute', left: 0, right: 0, top: -3, height: 6, pointerEvents: 'none' }}>
            {stops.map((s, i) => (
              <span key={`s${i}`} onClick={() => jump(s.idx)} title={`Paragem · ${s.durMin} min`}
                style={{ position: 'absolute', left: `${pct(s.idx)}%`, transform: 'translateX(-50%)', width: 6, height: 6, borderRadius: '50%', background: '#94a3b8', border: '1px solid #fff', cursor: 'pointer', pointerEvents: 'auto' }} />
            ))}
            {speeding.map((s, i) => (
              <span key={`v${i}`} onClick={() => jump(s.idx)} title={`Excesso · ${s.speed} km/h`}
                style={{ position: 'absolute', left: `${pct(s.idx)}%`, transform: 'translateX(-50%)', width: 6, height: 6, borderRadius: '50%', background: '#ef4444', border: '1px solid #fff', cursor: 'pointer', pointerEvents: 'auto' }} />
            ))}
          </div>
        )}
        <input type="range" min={0} max={total - 1} value={idx} onChange={onScrub}
          title="Arrastar para navegar"
          style={{ width: '100%', accentColor: '#2563eb', cursor: 'pointer' }} />
      </div>
      <div style={{ display: 'flex', gap: 2 }}>
        {SPEEDS.map((s) => (
          <button key={s} type="button" onClick={() => setSpeed(s)}
            title={`Velocidade ${SPEED_LABEL[s]}`}
            style={{
              ...btn, padding: '0 6px', minWidth: 24, height: 24, fontSize: '0.64rem', fontWeight: 800,
              background: speed === s ? '#2563eb' : '#e2e8f0', color: speed === s ? '#fff' : '#475569',
            }}>
            {SPEED_LABEL[s]}
          </button>
        ))}
      </div>
      <span style={{ fontSize: '0.62rem', fontWeight: 700, color: '#0f172a', minWidth: 168, textAlign: 'right', whiteSpace: 'nowrap' }}>
        <span style={{ color: '#94a3b8' }}>{idx + 1}/{total}</span>
        {' · '}{cur.timestamp ? fmtClock(new Date(cur.timestamp).getTime()) : '—'}
        {' · '}{Math.round(Number(cur.speed) || 0)} km/h
        {' · '}<span style={{ color: '#2563eb' }}>{cardinal(lastHeadingRef.current)}</span>
      </span>
    </div>
  );
}
